/* © 2017-2026 Booz Allen Hamilton Inc. All Rights Reserved. */

/**
 * SelectCustomerModal.jsx
 * Created by Hassan Riaz on 1/16/20.
 */

import React from 'react';
import axios from 'axios';
import PropTypes from 'prop-types';

import { getExternalCSRAuthHeader } from 'sarsaparilla/auth';

import {
    Button,
    ButtonGroup,
    StyledModal,
    ModalActions,
    Radio,
    RadioGroup,
} from 'sarsaparilla';
import CustomerResults from './CustomerResults';
import ExistingCustomerForm from './ExistingCustomerForm';
import NewCustomerForm from './NewCustomerForm';

export default class SelectCustomerModal extends React.PureComponent {
    static propTypes = {
        isOpen: PropTypes.bool,
        onRequestClose: PropTypes.func,
        csrSelectCustomer: PropTypes.func,
    };

    constructor(props) {
        super(props);

        this.state = {
            selectType: 'existing',
            results: [],
            searchError: '',
            newCustomerError: '',
        };
    }

    handleSelectTypeChange = (e) => {
        const selectType = e.target.value;
        this.setState({ selectType });
    };

    search = (type, term) => {
        this.setState(
            {
                searchError: '',
            },
            () => {
                const params = {};
                if (type === 'email') {
                    params.email = term;
                } else {
                    params.phone = term;
                }

                const url = `${process.env.API}/accounts/internal/search`;
                axios
                    .get(url, {
                        headers: {
                            Authorization: getExternalCSRAuthHeader(),
                        },
                        params,
                    })
                    .then((resp) => {
                        let results = [];
                        let error = '';
                        if (resp.data.account.account_id) {
                            const account = resp.data.account;
                            results.push(account);
                        } else if (resp.data.results.length > 0) {
                            const accounts = resp.data.results;
                            results = accounts;
                        } else {
                            error = 'There are no results for this search term';
                        }
                        this.setState({
                            results,
                            searchError: error,
                            newCustomerError: '',
                        });
                    })
                    .catch((e) => {
                        const resp = e.response;
                        let error =
                            'An unexpected error occurred, please try again later';
                        if (resp.status === 400) {
                            error = resp.data.error;
                        }
                        this.setState({
                            searchError: error,
                        });
                    });
            }
        );
    };

    createAndSelect = (
        firstName,
        lastName,
        cellPhone,
        email,
        hasNoEmail,
        homeAddress,
        addAddress
    ) => {
        this.setState(
            {
                newCustomerError: '',
            },
            () => {
                // Remove non digits/+ signs from the PhoneField formatting, so it matches what the reg fields
                // have elsewhere in the system
                const newCellPhone = cellPhone.replace(/[^\d+]/gi, '');

                let newEmail = email;
                if (hasNoEmail) {
                    newEmail = `${newCellPhone}@recreation.gov`;
                }

                const body = {
                    first_name: firstName,
                    last_name: lastName,
                    email: newEmail,
                    cell_phone: newCellPhone,
                };

                if (addAddress) {
                    body.home_address = homeAddress;
                }

                const url = `${process.env.API}/accounts/internal/account`;
                axios
                    .post(url, body, {
                        headers: {
                            Authorization: getExternalCSRAuthHeader(),
                        },
                    })
                    .then((resp) => {
                        const accountID = resp.data.account_id;
                        this.select(accountID);
                    })
                    .catch((e) => {
                        const resp = e.response;
                        let error =
                            'An unexpected error occurred, please try again later';
                        if (resp.status === 400) {
                            error = resp.data.error;
                        }
                        this.setState({
                            newCustomerError: error,
                        });
                    });
            }
        );
    };

    select = (accountID) => {
        this.props.csrSelectCustomer(accountID);
        this.props.onRequestClose();
    };

    clear = () => {
        this.setState({
            selectType: 'existing',
            results: [],
            searchError: '',
            newCustomerError: '',
        });
    };

    render() {
        let content = (
            <ExistingCustomerForm search={this.search} error={this.state.searchError} />
        );
        if (this.state.selectType === 'new') {
            content = (
                <NewCustomerForm
                    createAndSelect={this.createAndSelect}
                    error={this.state.newCustomerError}
                />
            );
        }

        if (this.state.results.length > 0) {
            content = (
                <CustomerResults results={this.state.results} select={this.select} />
            );
        }

        return (
            <StyledModal
                size="lg"
                isOpen={this.props.isOpen}
                heading="Select Customer"
                onRequestClose={this.props.onRequestClose}
            >
                <div className="mb-2">
                    <RadioGroup
                        name="select-type-group"
                        value={this.state.selectType}
                        onChange={this.handleSelectTypeChange}
                        isInline
                    >
                        <Radio id="existing" label="Existing User" value="existing" />
                        <Radio id="new" label="New User" value="new" />
                    </RadioGroup>
                </div>
                {content}
                <ModalActions>
                    <ButtonGroup isFullWidthOnMobile={false} isStretchedToFit>
                        <Button appearance="tertiary" onClick={this.props.onRequestClose}>
                            Cancel
                        </Button>
                        <Button appearance="tertiary" onClick={this.clear}>
                            Clear
                        </Button>
                    </ButtonGroup>
                </ModalActions>
            </StyledModal>
        );
    }
}
